import { useEffect, useState } from "react";
import { ConfigProvider, Input, Modal, Select } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useViewport } from "../../../../../hooks/useViewport";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
} from "../../modalPrimitives";
import { useUpdateInvoiceCustomer } from "../../../../../hooks/useInvoices";
import { useCustomers } from "../../../../../hooks/useCustomers";
import type { Customer } from "../../../../../api/customers";
import type { Invoice } from "../../../../../api/invoices";
import { isValidBgVat, isValidEIK } from "../../../../../lib/companyValidators";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useIsDarkTheme } from "../../../../../store/hooks";

// ДДС № — както при издаване: главни букви, без интервали
const normalizeVat = (raw: string): string =>
  raw.replace(/\s+/g, "").toUpperCase();

interface Props {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
}

/**
 * Редакция на получателя на ИЗДАДЕНА фактура (моливът в прегледа от
 * справките). Пипа само снимката `customer_*` върху записа — суми, редове,
 * номер и дати остават непроменени; за тях пътят е „Анулирай" + нова фактура.
 */
export const InvoiceCustomerEditModal = ({ open, invoice, onClose }: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const isDark = useIsDarkTheme();
  const message = useOrderingMessage();
  const updateMutation = useUpdateInvoiceCustomer();

  const [companyName, setCompanyName] = useState("");
  const [eik, setEik] = useState("");
  const [vat, setVat] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [mol, setMol] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  // „Изберете фирма" — както при издаване: търсене в запазените клиенти;
  // избор попълва всички полета. Нов ЕИК се добавя в регистъра при записа.
  const [customerSearch, setCustomerSearch] = useState("");
  const [pickedCustomerId, setPickedCustomerId] = useState<number | null>(null);
  const { data: customerOptions = [] } = useCustomers(customerSearch, {
    enabled: open,
  });

  const fillFromCustomer = (c: Customer) => {
    setPickedCustomerId(c.id);
    setCompanyName(c.company_name);
    setEik(c.eik);
    setVat(c.vat_number ?? "");
    setCity(c.city);
    setAddress(c.address);
    setMol(c.mol ?? "");
  };

  // При всяко отваряне започваме от записаното във фактурата
  useEffect(() => {
    if (!open || !invoice) return;
    setCompanyName(invoice.customer_name);
    setEik(invoice.customer_eik);
    setVat(invoice.customer_vat ?? "");
    setCity(invoice.customer_city);
    setAddress(invoice.customer_address);
    setMol(invoice.customer_mol ?? "");
    setCustomerSearch("");
    setPickedCustomerId(null);
    setShowErrors(false);
  }, [open, invoice]);

  if (!invoice) return null;

  const eikValid = isValidEIK(eik);
  const vatValid = !vat || isValidBgVat(vat);
  const requiredFilled =
    companyName.trim().length > 0 &&
    city.trim().length > 0 &&
    address.trim().length > 0;

  const errStatus = (invalid: boolean) =>
    showErrors && invalid ? ("error" as const) : undefined;

  const handleSave = async () => {
    setShowErrors(true);
    if (!eikValid) {
      message.error(t("invoiceCreate.errCustomerEik"));
      return;
    }
    if (!vatValid) {
      message.error(t("invoiceCreate.errCustomerVat"));
      return;
    }
    if (!requiredFilled) {
      message.error(t("invoiceCreate.errCustomerFields"));
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: invoice.id,
        data: {
          company_name: companyName.trim(),
          eik: eik.trim(),
          vat_number: vat.trim() || null,
          city: city.trim(),
          address: address.trim(),
          mol: mol.trim() || null,
        },
      });
      message.success(t("invoicePreview.editCustomerSuccess"));
      onClose();
    } catch (error) {
      message.error(
        getApiErrorMessage(error, t("invoicePreview.editCustomerError")),
      );
    }
  };

  const modalWidth = isMobile ? undefined : isTablet ? 460 : 520;

  return (
    // Локална antd тема (разрешеното изключение от „само Tailwind"):
    // антд-овите Input/Select са сини и с малък радиус по подразбиране —
    // тук ги връзваме към pos токените (accent, ctl, rounded-xl), за да
    // изглеждат като останалите полета в приложението.
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#C97C2C", // pos-accent
          colorBorder: isDark ? "#4d4437" : "#E5DED3", // pos-ctl (elevated — модал)
          borderRadius: 12,
        },
      }}
    >
      <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="bg-pos-card">
        <ModalHeader
          icon={<EditOutlined />}
          title={t("invoicePreview.editCustomer")}
          subtitle={t("invoicePreview.editCustomerSubtitle")}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          className={`bg-pos-canvas flex flex-col gap-3 ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-[18px]"
          }`}
        >
          <Field label={t("invoiceCreate.fieldPickCompany")}>
            <Select
              showSearch
              autoFocus
              placeholder={t("invoiceCreate.fieldPickCompanyPh")}
              className="w-full"
              value={pickedCustomerId ?? undefined}
              onSearch={(v) => setCustomerSearch(v)}
              onSelect={(id) => {
                const c = customerOptions.find((x) => x.id === id);
                if (c) fillFromCustomer(c);
              }}
              filterOption={false}
              notFoundContent={
                customerSearch
                  ? t("invoiceCreate.noCustomersForQuery")
                  : t("invoiceCreate.noCustomers")
              }
              allowClear
              onClear={() => setPickedCustomerId(null)}
              options={customerOptions.map((c) => ({
                value: c.id,
                label: `${c.company_name} · ЕИК ${c.eik}`,
              }))}
            />
          </Field>

          <Field label={t("invoiceCreate.fieldName")} required>
            <Input
              status={errStatus(!companyName.trim())}
              value={companyName}
              maxLength={255}
              placeholder={t("invoiceCreate.phCustomerName")}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setPickedCustomerId(null);
              }}
              onPressEnter={handleSave}
            />
          </Field>

          <div
            className={`grid gap-2.5 ${isNarrow ? "grid-cols-1" : "grid-cols-2"}`}
          >
            <Field label="ЕИК №" required>
              <Input
                status={errStatus(!eikValid)}
                value={eik}
                maxLength={13}
                inputMode="numeric"
                placeholder={t("invoiceCreate.phCustomerEik")}
                onChange={(e) => {
                  setEik(e.target.value.replace(/\D/g, "").slice(0, 13));
                  setPickedCustomerId(null);
                }}
                onPressEnter={handleSave}
              />
            </Field>
            <Field label={t("invoiceCreate.fieldVat")}>
              <Input
                status={errStatus(!vatValid)}
                value={vat}
                maxLength={20}
                placeholder="BG123456789"
                onChange={(e) => {
                  setVat(normalizeVat(e.target.value));
                  setPickedCustomerId(null);
                }}
                onPressEnter={handleSave}
              />
            </Field>
            <Field label={t("invoiceCreate.fieldCity")} required>
              <Input
                status={errStatus(!city.trim())}
                value={city}
                maxLength={255}
                placeholder={t("invoiceCreate.phCustomerCity")}
                onChange={(e) => {
                  setCity(e.target.value);
                  setPickedCustomerId(null);
                }}
                onPressEnter={handleSave}
              />
            </Field>
            <Field label={t("invoiceCreate.fieldMol")}>
              <Input
                value={mol}
                maxLength={255}
                placeholder={t("invoiceCreate.phCustomerMol")}
                onChange={(e) => {
                  setMol(e.target.value);
                  setPickedCustomerId(null);
                }}
                onPressEnter={handleSave}
              />
            </Field>
          </div>

          <Field label={t("invoiceCreate.fieldAddress")} required>
            <Input
              status={errStatus(!address.trim())}
              value={address}
              maxLength={500}
              placeholder={t("invoiceCreate.phCustomerAddress")}
              onChange={(e) => {
                setAddress(e.target.value);
                setPickedCustomerId(null);
              }}
              onPressEnter={handleSave}
            />
          </Field>
        </div>

        <ModalFooter isNarrow={isNarrow} narrowFirst>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={t("invoiceCreate.save")}
            onClick={handleSave}
            loading={updateMutation.isPending}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>
      </Modal>
    </ConfigProvider>
  );
};

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[5px]">
      <span className="text-[11px] font-semibold tracking-[.2px] text-pos-soft">
        {label}
        {required && <span className="ml-[3px] text-pos-danger-ink">*</span>}
      </span>
      {children}
    </div>
  );
}

export default InvoiceCustomerEditModal;
